/**
 * Mock-генератор встреч: детерминированный (seeded RNG), 60 встреч за
 * последние 30 дней (от `MOCK_TODAY`) с разными типами/менеджерами/оценками.
 * Источник данных в mock-режиме для страницы «Встречи».
 */
import type {
  Meeting,
  MeetingType,
  MeetingProcessingStatus,
  MeetingResult,
  MeetingCriterion,
  MeetingObjection,
} from '@entities/meeting/model/types';
import {MOCK_TODAY} from '@lib/utils';

// ─── Seeded RNG ────────────────────────────────────────────────────────────
let seed = 4242;
const sr = () => {
  seed = (seed * 1664525 + 1013904223) & 0xffffffff;
  return (seed >>> 0) / 0xffffffff;
};
const rndInt = (min: number, max: number) =>
  Math.floor(sr() * (max - min + 1)) + min;
const rndOf = <T>(arr: T[]): T => arr[Math.floor(sr() * arr.length)];

// ─── Справочники ───────────────────────────────────────────────────────────
const MANAGER_IDS = [
  'emp-1',
  'emp-2',
  'emp-3',
  'emp-4',
  'emp-5',
  'emp-6',
  'emp-7',
  'emp-8',
];
const MANAGER_NAMES = [
  'Алексей К.',
  'Мария П.',
  'Иван С.',
  'Ольга Т.',
  'Дмитрий Р.',
  'Наталья В.',
  'Сергей М.',
  'Анна Л.',
];

const CLIENT_NAMES = [
  'ООО Лидер',
  'ИП Смирнов',
  'АО Прогресс',
  'ООО Техно',
  'ИП Кузнецов',
  'ОАО Меркурий',
  'ООО Старт',
  'ИП Попова',
  'АО Альфа',
  'ООО Бета',
  'ИП Новиков',
  'АО Гамма',
];

const PLACES = [
  'Москва, ул. Тверская, 12, офис 405',
  'Санкт-Петербург, Невский пр., 28',
  'Москва, БЦ Сити, башня А, 12 этаж',
  'Казань, ул. Баумана, 15',
  'Екатеринбург, ул. Ленина, 22',
  'Москва, переговорная «Север»',
];

const NEXT_STEPS = [
  'Подготовить КП',
  'Согласовать условия',
  'Подписать договор',
  'Следующая встреча в пятницу',
  'Отправить расчёт',
  'Связаться через неделю',
  null,
];

const MAIN_OBJECTIONS = [
  'Нет потребности',
  'Думаю, посоветуюсь',
  'Уже работаем с другими',
  'Не уверен в качестве',
  'Сейчас не в приоритете',
];

const SUMMARIES = [
  'Клиент из дилерского центра, интересуется условиями сотрудничества.',
  'Презентация продукта, обсуждение тарифов и сроков.',
  'Клиент уточнил детали заказа, попросил расчёт на почту.',
  'Согласована следующая встреча для подписания договора.',
  'Проведена демонстрация сервиса, клиент доволен.',
  'Клиент запросил нестандартные условия, требуется согласование с РОПом.',
  'Обсуждали интеграцию с текущей CRM клиента.',
];

const RECOMMENDATIONS = [
  'Подготовить индивидуальное КП с учётом объёмов клиента.',
  'Согласовать условия с руководителем.',
  'Зафиксировать следующий шаг с конкретной датой.',
  'Отправить материалы и календарь встреч.',
  'Провести вторую встречу с участием технического эксперта.',
  null,
];

const PROCESSING_STATUSES: MeetingProcessingStatus[] = [
  'ready',
  'ready',
  'ready',
  'ready',
  'ready',
  'partial',
  'failed',
  'preprocessing',
];
const CRITERIA_NAMES = [
  'Подготовка',
  'Презентация',
  'Выявление потребности',
  'Работа с возражениями',
  'Договорённости',
  'Следующий шаг',
];
const CALL_STATUS_VALUES = ['sale', 'noresult', 'sale', 'noresult', 'sale'];

// ─── Генерация ─────────────────────────────────────────────────────────────
const generateMeetings = (): Meeting[] => {
  const result: Meeting[] = [];

  for (let i = 1; i <= 60; i++) {
    const managerIdx = rndInt(0, 7);
    const type: MeetingType = sr() > 0.5 ? 'offline' : 'online';
    const callStatus = rndOf(CALL_STATUS_VALUES) as
      | 'sale'
      | 'noresult';
    const score = Math.round((3 + sr() * 7) * 10) / 10;
    const isTargeted = callStatus === 'sale' || (callStatus === 'noresult' && sr() > 0.4);
    const daysAgo = rndInt(0, 30);
    const date = new Date(MOCK_TODAY);
    date.setDate(date.getDate() - daysAgo);
    date.setHours(rndInt(9, 18), rndInt(0, 59));

    const criteria: MeetingCriterion[] = CRITERIA_NAMES.map((name) => ({
      name,
      type: 'number' as const,
      score: Math.round((2 + sr() * 8) * 10) / 10,
    }));

    const objections: MeetingObjection[] = [
      'Дорого',
      'Уже работаем с другими',
      'Нет времени',
      'Нужно подумать',
    ]
      .slice(0, rndInt(0, 3))
      .map((name) => ({
        name,
        identified: sr() > 0.2,
        handled: sr() > 0.4,
      }));

    // Маркетинговый исход (симметрично звонкам).
    let meetingResult: MeetingResult | null = null;
    if (isTargeted) {
      if (callStatus === 'sale')
        meetingResult = sr() > 0.45 ? 'sale' : 'contract';
      else meetingResult = 'refusal';
    }

    const hasObjection = isTargeted && sr() > 0.35;
    const mainObjection = hasObjection ? rndOf(MAIN_OBJECTIONS) : null;
    const processingStatus = rndOf(PROCESSING_STATUSES);

    const phone = `+7 9${rndInt(10, 99)} ${rndInt(100, 999)}-${rndInt(
      10,
      99,
    )}-${rndInt(10, 99)}`;

    result.push({
      id: `OV-${1000 + i}`,
      type,
      isTargeted,
      managerId: MANAGER_IDS[managerIdx],
      manager: MANAGER_NAMES[managerIdx],
      client: rndOf(CLIENT_NAMES),
      clientPhone: phone,
      occurredAt: date.toISOString(),
      durationSeconds: rndInt(900, 3600), // 15–60 минут (встречи длиннее звонков)
      callStatus,
      result: meetingResult,
      resultText: meetingResult === 'refusal' ? 'Клиент отказался' : null,
      score,
      criteria,
      objections,
      mainObjection,
      scriptCompliance: sr() > 0.35,
      promoApplied: sr() > 0.55,
      nextStep: isTargeted ? rndOf(NEXT_STEPS) : null,
      summary: isTargeted
        ? rndOf(SUMMARIES)
        : 'Клиент не пришёл на встречу.',
      recommendation: isTargeted ? rndOf(RECOMMENDATIONS) : null,
      processingStatus,
      resultStatus:
        processingStatus === 'ready'
          ? 'succeeded'
          : processingStatus === 'preprocessing'
            ? 'running'
            : 'pending',
      hasMedia: sr() > 0.3,
      place: type === 'offline' ? rndOf(PLACES) : undefined,
      meetingUrl:
        type === 'online' ? `https://meet.example.com/ov-${1000 + i}` : undefined,
    });
  }

  return result;
};

export const MOCK_MEETINGS = generateMeetings();

/** Детальная встреча для модалки — фиксированный id, по аналогии с MOCK_DETAILED_CALL. */
export const MOCK_MEETING_DETAIL: Meeting = {
  id: 'OV-1841',
  type: 'offline',
  isTargeted: true,
  managerId: 'emp-1',
  manager: 'Алексей К.',
  client: 'ООО Лидер',
  clientPhone: '+7 916 123-45-67',
  occurredAt: `${MOCK_TODAY}T14:00:00.000Z`,
  durationSeconds: 2700, // 45 минут
  callStatus: 'sale',
  result: 'sale',
  resultText: null,
  score: 8.4,
  criteria: [
    {name: 'Подготовка', type: 'number', score: 9.0},
    {name: 'Презентация', type: 'number', score: 8.5},
    {name: 'Выявление потребности', type: 'number', score: 9.0},
    {name: 'Работа с возражениями', type: 'number', score: 7.5},
    {name: 'Договорённости', type: 'number', score: 8.5},
    {name: 'Следующий шаг', type: 'number', score: 9.0},
  ],
  objections: [
    {name: 'Дорого', identified: true, handled: true},
    {name: 'Сроки внедрения', identified: true, handled: true},
  ],
  mainObjection: 'Дорого',
  scriptCompliance: true,
  promoApplied: false,
  nextStep: 'Подготовить КП',
  summary:
    'Проведена очная встреча с клиентом. Обсудили условия сотрудничества, провели демонстрацию. Клиент готов рассмотреть предложение и подписать договор.',
  recommendation:
    'Подготовить финальное КП с учётом объёмов клиента и отправить до конца недели.',
  processingStatus: 'ready',
  resultStatus: 'succeeded',
  hasMedia: true,
  place: 'Москва, БЦ Сити, башня А, 12 этаж',
};